import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Info, TrendingUp, TrendingDown, Minus, Loader2 } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';

// Semantic keywords for calculating favorability
const POSITIVE_KEYWORDS = ['strongly agree', 'agree', 'very favorable', 'favorable', 'very likely', 'likely', 'support', 'strongly support', 'yes', 'excellent', 'very good', 'good', 'satisfied', 'very satisfied'];
const NEGATIVE_KEYWORDS = ['strongly disagree', 'disagree', 'very unfavorable', 'unfavorable', 'very unlikely', 'unlikely', 'oppose', 'strongly oppose', 'no', 'poor', 'very poor', 'dissatisfied', 'very dissatisfied'];

export default function CrossDemographicsMatrix({ 
  data, 
  responseScale, 
  demographic1, 
  demographic2,
  onCellClick,
  loading 
}) {
  const [metric, setMetric] = useState('favorable'); // 'favorable', 'net', 'entropy'

  // Convert data object to array format if needed
  const dataArray = useMemo(() => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    
    // Convert object format { "Male × 25-34": {response: count} } to array
    return Object.entries(data).map(([key, responses]) => ({
      name: key,
      ...responses
    }));
  }, [data]);

  // Extract unique values for each demographic
  const values1 = useMemo(() => {
    if (!dataArray || dataArray.length === 0) return [];
    return [...new Set(dataArray.map(d => d.name.split(' × ')[0]))];
  }, [dataArray]);
  
  const values2 = useMemo(() => {
    if (!dataArray || dataArray.length === 0) return [];
    return [...new Set(dataArray.map(d => d.name.split(' × ')[1]))];
  }, [dataArray]);

  // Calculate favorable/unfavorable/neutral based on response scale
  const categorizeResponse = (response) => {
    const lower = response.toLowerCase().trim();
    
    if (POSITIVE_KEYWORDS.some(k => lower.includes(k))) return 'favorable';
    if (NEGATIVE_KEYWORDS.some(k => lower.includes(k))) return 'unfavorable';
    return 'neutral';
  };

  // Calculate metric for a cell
  const calculateMetric = (value1, value2) => {
    const cellData = dataArray.find(d => 
      d.name === `${value1} × ${value2}`
    );
    
    if (!cellData) return null;

    let totalCount = 0;
    let favorableCount = 0;
    let unfavorableCount = 0;
    
    responseScale.forEach(response => {
      const count = cellData[response] || 0;
      totalCount += count;
      
      const category = categorizeResponse(response);
      if (category === 'favorable') favorableCount += count;
      if (category === 'unfavorable') unfavorableCount += count;
    });

    if (totalCount === 0) return null;

    if (metric === 'favorable') {
      return {
        value: ((favorableCount / totalCount) * 100).toFixed(1),
        total: totalCount,
        raw: favorableCount
      };
    } else if (metric === 'net') {
      const net = ((favorableCount - unfavorableCount) / totalCount) * 100;
      return {
        value: net.toFixed(1),
        total: totalCount,
        favorable: favorableCount,
        unfavorable: unfavorableCount
      };
    } else if (metric === 'entropy') {
      // Calculate entropy
      let entropy = 0;
      responseScale.forEach(response => {
        const count = cellData[response] || 0;
        if (count > 0) {
          const p = count / totalCount;
          entropy -= p * Math.log2(p);
        }
      });
      return {
        value: entropy.toFixed(2),
        total: totalCount
      };
    }
  };

  // Get color for cell based on metric value
  const getCellColor = (metricData) => {
    if (!metricData) return 'bg-muted text-muted-foreground';
    
    const val = parseFloat(metricData.value);
    
    if (metric === 'favorable') {
      if (val >= 70) return 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300 font-semibold';
      if (val >= 50) return 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400';
      if (val >= 30) return 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400';
      return 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400';
    } else if (metric === 'net') {
      if (val >= 40) return 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300 font-semibold';
      if (val >= 10) return 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400';
      if (val >= -10) return 'bg-muted text-foreground';
      if (val >= -40) return 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400';
      return 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300 font-semibold';
    } else if (metric === 'entropy') {
      if (val >= 2) return 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 font-semibold';
      if (val >= 1.5) return 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400';
      if (val >= 1) return 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400';
      return 'bg-muted text-foreground';
    }
  };

  // Get trend indicator for net metric
  const getTrendIcon = (val) => {
    if (metric !== 'net') return null;
    const numVal = parseFloat(val);
    if (numVal > 10) return <TrendingUp className="w-3 h-3 inline ml-1" />;
    if (numVal < -10) return <TrendingDown className="w-3 h-3 inline ml-1" />;
    return <Minus className="w-3 h-3 inline ml-1 opacity-50" />;
  };

  // Check if loading
  if (loading) {
    return (
      <Card className="border border-border">
        <CardContent className="p-12 flex flex-col items-center justify-center text-muted-foreground">
          <Loader2 className="w-8 h-8 animate-spin mb-3 opacity-50" />
          <p className="text-sm font-medium">Generating cross-demographic analysis...</p>
          <p className="text-xs mt-1">This may take a moment for large populations</p>
        </CardContent>
      </Card>
    );
  }

  // Check if data is available
  if (!dataArray || dataArray.length === 0 || values1.length === 0 || values2.length === 0) {
    return (
      <Card className="border border-border">
        <CardContent className="p-6 text-center text-muted-foreground">
          <p>Select two demographic attributes to view cross-demographic analysis.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border border-border">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="text-lg">Cross-Demographics Matrix</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              {demographic1} x {demographic2}
            </p>
          </div>
          
          {/* Metric Selector */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Show:</span>
            <Select value={metric} onValueChange={setMetric}>
              <SelectTrigger className="w-40 h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="favorable">% Favorable</SelectItem>
                <SelectItem value="net">Net Favorable</SelectItem>
                <SelectItem value="entropy">Entropy</SelectItem>
              </SelectContent>
            </Select>
            
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="w-4 h-4 text-slate-400 cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="left" className="max-w-xs">
                  <p className="text-xs font-semibold mb-1">
                    {metric === 'favorable' && '% Favorable'}
                    {metric === 'net' && 'Net Favorable'}
                    {metric === 'entropy' && 'Entropy (Diversity)'}
                  </p>
                  <p className="text-xs">
                    {metric === 'favorable' && 'Percentage of favorable responses (Agree, Favorable, Support, etc.)'}
                    {metric === 'net' && 'Net score: Favorable minus Unfavorable responses, as percentage'}
                    {metric === 'entropy' && 'Response diversity measure. Higher = more spread across options'}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b-2 border-border">
                <th className="text-left p-2 text-xs font-semibold text-muted-foreground bg-muted sticky left-0 z-10 min-w-[120px]">
                  {demographic1} \ {demographic2}
                </th>
                {values2.map((val2, idx) => (
                  <th 
                    key={idx} 
                    className="text-center p-2 text-xs font-semibold text-foreground bg-muted min-w-[90px]"
                  >
                    {val2}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {values1.map((val1, rowIdx) => (
                <tr key={rowIdx} className="border-b border-border">
                  <td className="p-2 text-xs font-medium text-foreground bg-muted/50 sticky left-0 z-10">
                    {val1}
                  </td>
                  {values2.map((val2, colIdx) => {
                    const metricData = calculateMetric(val1, val2);
                    const colorClass = getCellColor(metricData);
                    
                    return (
                      <td key={colIdx} className="p-1">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div 
                                className={`
                                  rounded px-2 py-2 text-center text-xs transition-all 
                                  hover:scale-105 hover:shadow-sm cursor-pointer
                                  ${colorClass}
                                  ${!metricData && 'cursor-not-allowed'}
                                `}
                                onClick={() => metricData && onCellClick && onCellClick(val1, val2)}
                              >
                                {metricData ? (
                                  <>
                                    {metric === 'net' && parseFloat(metricData.value) > 0 && '+'}
                                    {metricData.value}
                                    {metric !== 'entropy' && '%'}
                                    {getTrendIcon(metricData.value)}
                                    <div className="text-[10px] opacity-70 mt-0.5">
                                      n={metricData.total}
                                    </div>
                                  </>
                                ) : (
                                  <span className="opacity-50">—</span>
                                )}
                              </div>
                            </TooltipTrigger>
                            {metricData && (
                              <TooltipContent side="top" className="max-w-xs">
                                <p className="text-xs font-semibold mb-1">
                                  {val1} × {val2}
                                </p>
                                <p className="text-xs">
                                  {metric === 'favorable' && `${metricData.raw} favorable out of ${metricData.total} responses`}
                                  {metric === 'net' && `Net: ${metricData.value}% (${metricData.favorable} favorable - ${metricData.unfavorable} unfavorable)`}
                                  {metric === 'entropy' && `Diversity: ${metricData.value} (n=${metricData.total})`}
                                </p>
                              </TooltipContent>
                            )}
                          </Tooltip>
                        </TooltipProvider>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Legend */}
        <div className="mt-4 flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
          <span className="font-medium">Legend:</span>
          {metric === 'favorable' && (
            <>
              <div className="flex items-center gap-1">
                <div className="w-5 h-4 bg-red-50 rounded border border-red-200"></div>
                <span>&lt;30%</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-5 h-4 bg-amber-50 rounded border border-amber-200"></div>
                <span>30-50%</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-5 h-4 bg-emerald-50 rounded border border-emerald-200"></div>
                <span>50-70%</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-5 h-4 bg-emerald-100 rounded border border-emerald-300"></div>
                <span>&gt;70%</span>
              </div>
            </>
          )}
          {metric === 'net' && (
            <>
              <div className="flex items-center gap-1">
                <div className="w-5 h-4 bg-red-100 rounded border border-red-300"></div>
                <span>&lt;-40%</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-5 h-4 bg-red-50 rounded border border-red-200"></div>
                <span>-40 to -10%</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-5 h-4 bg-slate-100 rounded border border-slate-200"></div>
                <span>-10 to +10%</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-5 h-4 bg-emerald-50 rounded border border-emerald-200"></div>
                <span>+10 to +40%</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-5 h-4 bg-emerald-100 rounded border border-emerald-300"></div>
                <span>&gt;+40%</span>
              </div>
            </>
          )}
          {metric === 'entropy' && (
            <>
              <div className="flex items-center gap-1">
                <div className="w-5 h-4 bg-slate-100 rounded border border-slate-200"></div>
                <span>&lt;1 (Low)</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-5 h-4 bg-amber-50 rounded border border-amber-200"></div>
                <span>1-1.5 (Med)</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-5 h-4 bg-blue-50 rounded border border-blue-200"></div>
                <span>1.5-2 (High)</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-5 h-4 bg-blue-100 rounded border border-blue-300"></div>
                <span>&gt;2 (Very High)</span>
              </div>
            </>
          )}
        </div>

        {/* Info note */}
        <div className="mt-3 p-2 bg-blue-50/50 dark:bg-blue-900/10 rounded-md border border-blue-100 dark:border-blue-800">
          <p className="text-xs text-blue-800 dark:text-blue-300">
            <strong>Tip:</strong> Click on any cell to drill down into detailed distribution for that segment.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
